/**
 * The write-side check on theme colors: what `storefront.update` (the page theme),
 * `storefront.setTermlyTheme` and `platform.termly.setTheme` (the cookie banner theme) refuse
 * before a color is stored.
 *
 * Every reader sanitizes again through `safeThemeColor`, so a stored color it refuses is dropped
 * and the token falls back; it never reaches the page. This check is how an editor learns that on
 * save, instead of watching the storefront quietly ignore the value. Read paths keep the plain
 * schemas (`lootThemeSchema`, `cookieBannerThemeSchema`): a row stored before this check, or one
 * a tightened allowlist now refuses, still has to parse.
 *
 * Zod by type only, so this module adds nothing to a bundle that never validates.
 */
import type { z } from "zod";

import { safeThemeColor } from "./storefrontThemeVars.js";

/** A rarity tier's `r,g,b` triplet, the second form `theme.rarity` takes (`rarityColor.ts`). */
const RARITY_TRIPLET = /^\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}$/;

/** A theme color a reader keeps: blank (= unset, the token keeps its default) or a safe color. */
export function isAcceptedThemeColor(value: string): boolean {
  return value.trim() === "" || safeThemeColor(value) !== null;
}

/** A rarity override a reader keeps: what `isAcceptedThemeColor` keeps, or an `r,g,b` triplet. */
export function isAcceptedRarityColor(value: string): boolean {
  return isAcceptedThemeColor(value) || RARITY_TRIPLET.test(value.trim());
}

/**
 * A `superRefine` for a theme's colors: one issue per token (`tokens`) or rarity tier (`rarity`)
 * a reader would drop, at that key's path and naming it, so the editor is told which field to fix.
 * Structural, so the page theme and the cookie banner theme share it.
 */
export function refuseUnacceptedThemeColors(
  theme: {
    tokens?: Partial<Record<string, string>>;
    rarity?: Partial<Record<string, string>>;
  },
  ctx: z.RefinementCtx,
): void {
  for (const [key, value] of Object.entries(theme.tokens ?? {})) {
    if (typeof value !== "string" || isAcceptedThemeColor(value)) continue;
    ctx.addIssue({
      code: "custom",
      message: `"${key}" is not a color the storefront accepts: use a hex, rgb(), hsl() or oklch() value, or a color name`,
      path: ["tokens", key],
    });
  }
  for (const [key, value] of Object.entries(theme.rarity ?? {})) {
    if (typeof value !== "string" || isAcceptedRarityColor(value)) continue;
    ctx.addIssue({
      code: "custom",
      message: `rarity tier "${key}" is not a color the storefront accepts: use a hex or r,g,b value`,
      path: ["rarity", key],
    });
  }
}
